import axios, { AxiosError } from 'axios'
import { adoptServerLocale, getActiveLocale } from '@/i18n/locale'
import { translate, type TranslationKey } from '@/i18n/dictionary'
import { isModuleName, MODULE_DISABLED_EVENT, type ModuleDisabledDetail } from '@/lib/modules'

/** Fired on a 403 that asks for two-factor; see the response interceptor. */
export const TWO_FACTOR_REQUIRED_EVENT = 'kapro:two-factor-required'
import type { ModuleName } from '@/types/api'

/**
 * Translate outside React.
 *
 * These interceptors run with no component around them, so there is no
 * `useT()` to reach for — the locale comes from the module-level value the
 * request interceptor is already reading.
 */
const t = (key: TranslationKey) => translate(getActiveLocale(), key)

const TOKEN_KEY = 'kapro.token'

export const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL ?? '/api',
  headers: { Accept: 'application/json' },
  timeout: 30_000,
})

/*
 * Where the session token lives.
 *
 * localStorage survives closing the browser; sessionStorage does not. That
 * difference is the whole of "remember me", and it matters here because some of
 * these devices are shared terminals on the shop floor — leaving a live session
 * behind means the next person files reports under someone else's name.
 *
 * sessionStorage is checked first: it is the more specific, more recent choice,
 * so a deliberate "do not remember me" can never be shadowed by a token an
 * earlier session left in localStorage.
 */

/**
 * The token this tab is actually using, held in memory.
 *
 * **This is what makes a signed-in tab keep its own identity**, and it was added
 * after watching the alternative happen. Requests used to read storage on every
 * call, and `localStorage` is shared by every tab on the origin — so a second
 * person signing in anywhere in the browser, with "remember me" on as it is by
 * default, silently repointed every already-open tab at *their* account.
 *
 * The open tab carried on looking normal. It still showed the first worker's
 * name in the header, because that came from React state, while everything it
 * sent went out as the second user. A report filed from that tab was recorded
 * against the wrong person — which on a floor where accepted improvements earn
 * points is not a cosmetic problem.
 *
 * Storage is now consulted only to *restore* a session when a tab loads. Once a
 * tab knows who it is, nothing another tab writes can change that; a reload is
 * what picks up the newer session, which is both expected and visible.
 */
let activeToken: string | null = null

export function getToken(): string | null {
  if (activeToken) return activeToken

  activeToken = sessionStorage.getItem(TOKEN_KEY) ?? localStorage.getItem(TOKEN_KEY)

  return activeToken
}

export function setToken(token: string, remember = true): void {
  activeToken = token

  // Written to exactly one store, with the other cleared, so the two can never
  // disagree about which session is current.
  const [chosen, other] = remember
    ? [localStorage, sessionStorage]
    : [sessionStorage, localStorage]

  chosen.setItem(TOKEN_KEY, token)
  other.removeItem(TOKEN_KEY)
}

export function clearToken(): void {
  activeToken = null
  localStorage.removeItem(TOKEN_KEY)
  sessionStorage.removeItem(TOKEN_KEY)
}

/*
 * Support sessions.
 *
 * When the owner signs in as a customer's user, two tokens exist at once: the
 * one being used, and the owner's own to come back to. Neither can be lost.
 *
 * The support token goes to sessionStorage, and `localStorage` is deliberately
 * **not** touched — which is why this cannot go through `setToken`, whose whole
 * job is to make the two stores agree. A support session belongs to the tab it
 * was opened in: the owner's other tabs, and their remembered session, carry on
 * exactly as they were. `setToken(token, false)` would have cleared the
 * remembered token and signed them out everywhere else in order to look at one
 * customer's screen.
 *
 * sessionStorage rather than memory so a reload does not silently drop back to
 * the owner's own account — the session survives F5, and ends when the tab does
 * or when the hour on the token runs out.
 */
const SUPPORT_KEY = 'kapro.support'

export function beginImpersonation(token: string): void {
  /*
   * Empty string when the owner's own token lives in localStorage: there is
   * nothing in sessionStorage to put back, and the key's *presence* is what
   * marks the tab as impersonating. Both facts are recorded by writing it.
   */
  sessionStorage.setItem(SUPPORT_KEY, sessionStorage.getItem(TOKEN_KEY) ?? '')

  activeToken = token
  sessionStorage.setItem(TOKEN_KEY, token)
}

export function endImpersonation(): void {
  const previous = sessionStorage.getItem(SUPPORT_KEY)
  sessionStorage.removeItem(SUPPORT_KEY)

  if (previous) {
    // The owner was on an unremembered session; put it back where it was.
    sessionStorage.setItem(TOKEN_KEY, previous)
    activeToken = previous

    return
  }

  // Otherwise their own token never left localStorage. Clearing the session
  // copy uncovers it again.
  sessionStorage.removeItem(TOKEN_KEY)
  activeToken = localStorage.getItem(TOKEN_KEY)
}

/**
 * Whether this tab is inside a support session.
 *
 * Only ever used to decide whether to *offer* the way out. Whether one is
 * actually running is the server's answer, on every `/auth/me` — a client that
 * decided this for itself could hold the banner open over a token that had
 * already expired, or worse, fail to show it over one that had not.
 */
export function isImpersonating(): boolean {
  return sessionStorage.getItem(SUPPORT_KEY) !== null
}

/**
 * Whether this session was the "remember me" kind.
 *
 * Exported so nothing else has to know the storage key. `lastRoute.ts` used to
 * check `localStorage.getItem('kapro.token')` with the string written out again,
 * which works right up until the key changes — and then routes quietly stop
 * being remembered, with nothing failing to say so.
 */
export function hasRememberedSession(): boolean {
  return localStorage.getItem(TOKEN_KEY) !== null
}

api.interceptors.request.use((config) => {
  const token = getToken()
  if (token) config.headers.Authorization = `Bearer ${token}`

  /*
   * Set explicitly on every request, overwriting whatever the browser would
   * have sent.
   *
   * That overwrite is the point. `Accept-Language` is not removable from a
   * fetch — the browser fills it in from the operating system — and on a
   * shop-floor Android flashed with a generic ROM the operating system says
   * `en-US` no matter who is holding the phone. Leaving it alone would tell the
   * server a Bosnian worker had asked for English.
   *
   * The server treats the header as the *device's* opinion and ranks a saved
   * account preference above it, so this is not the only guard — but it is the
   * one that makes the sign-in screen, where there is no account yet, come up
   * in the right language.
   */
  config.headers['Accept-Language'] = getActiveLocale()

  return config
})

/**
 * Thrown for anything the API rejects, with the field errors kept intact.
 *
 * Fields are declared and assigned explicitly rather than as constructor
 * parameter properties — `erasableSyntaxOnly` in tsconfig.app.json forbids that
 * shorthand, because it is syntax that cannot be erased by a plain transpiler.
 */
export class ApiError extends Error {
  readonly status: number
  readonly errors: Record<string, string[]>
  readonly isOffline: boolean
  /**
   * The module the company has switched off, when that is why this failed.
   *
   * Set from the `module` the server puts in a 403's body. A screen that gets
   * one should say "not switched on for your company" rather than "you are not
   * allowed" — the person asking did nothing wrong, and neither did anybody
   * else.
   */
  readonly module: ModuleName | null

  constructor(
    message: string,
    status: number,
    errors: Record<string, string[]> = {},
    isOffline = false,
    module: ModuleName | null = null,
  ) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.errors = errors
    this.isOffline = isOffline
    this.module = module
  }

  /** First validation message for a field, for rendering under an input. */
  fieldError(field: string): string | undefined {
    return this.errors[field]?.[0]
  }

  /**
   * The first message for every field at once, keyed as the server keyed
   * them — `name`, `limits.maxUsers` — for a form that puts each under its
   * own input rather than one toast the reader has to map back to a field.
   */
  fieldErrors(): Record<string, string> {
    return Object.fromEntries(
      Object.entries(this.errors).map(([field, messages]) => [field, messages[0]]),
    )
  }
}

/**
 * Download a file from an authenticated endpoint.
 *
 * A plain `<a href>` cannot be used: authentication is a bearer token, and a
 * browser navigation carries no Authorization header. So the file is fetched as
 * a blob through the same axios instance — interceptors, token and error
 * handling all included — and then handed to the browser as an object URL.
 */
export async function downloadFile(
  path: string,
  params?: Record<string, unknown>,
  fallbackName = 'kapro-izvjestaj',
): Promise<void> {
  const response = await api.get<Blob>(path, {
    params,
    responseType: 'blob',
    // Exports can be slow — a year of submissions is a real amount of work.
    timeout: 120_000,
  })

  // Prefer the filename the server chose; it carries the timestamp.
  const disposition = response.headers['content-disposition'] as string | undefined
  const match = disposition?.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i)
  const filename = match ? decodeURIComponent(match[1]) : fallbackName

  const url = URL.createObjectURL(response.data)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  link.remove()

  // Revoking immediately can cancel the download in some browsers; a short
  // delay is the usual pragmatic fix. Without it the blob leaks for the life
  // of the page.
  setTimeout(() => URL.revokeObjectURL(url), 10_000)
}

/**
 * An error response to a blob request arrives as a Blob, not JSON, so the
 * message has to be read back out before the interceptor can use it.
 */
async function parseBlobError(
  blob: Blob,
): Promise<{ message?: string; errors?: Record<string, string[]>; module?: unknown }> {
  try {
    return JSON.parse(await blob.text())
  } catch {
    return {}
  }
}

api.interceptors.response.use(
  (response) => {
    /*
     * Learn the language from the server, where nobody has chosen one.
     *
     * The server resolves from facts this client cannot see — the account's
     * saved preference and the company-wide default — so a device signing in
     * for the first time would otherwise sit on the fallback until somebody
     * noticed the switcher. `adoptServerLocale` refuses to act once a
     * deliberate choice exists, which is what stops it undoing the switch on
     * the very response that confirmed it.
     */
    adoptServerLocale(response.headers['content-language'] as string | undefined)

    return response
  },
  async (
    error: AxiosError<{ message?: string; errors?: Record<string, string[]>; module?: unknown }>,
  ) => {
    // No response at all means the request never reached the server — on the
    // factory floor that is almost always the wifi, not a bug. Say so plainly.
    if (!error.response) {
      throw new ApiError(t('error.offline'), 0, {}, true)
    }

    const { status } = error.response

    /*
     * A failed export comes back as a Blob because the request asked for one,
     * even though the body is actually JSON. Without unwrapping it here the
     * user is shown "[object Blob]" instead of the real reason.
     */
    const data =
      error.response.data instanceof Blob
        ? await parseBlobError(error.response.data)
        : error.response.data

    // An expired or revoked token: drop it and send the user back to login.
    // Skipped on the login request itself, where 401/422 means bad credentials.
    if (status === 401 && !error.config?.url?.includes('/auth/login')) {
      /*
       * A support token that has run out its hour is not a lost session.
       *
       * `clearToken()` wipes both stores, so without this the expiry of a
       * one-hour support token would sign the owner out of their *own* account
       * as well and land them on the login screen — punishing them for reading
       * a customer's screen for too long. Stepping back out returns them to
       * their own session, which is where they were before and is still valid.
       */
      if (isImpersonating()) {
        endImpersonation()
        window.location.href = '/vlasnik'

        return Promise.reject(
          new ApiError(t('error.supportSessionExpired'), 401, {}, false),
        )
      }

      clearToken()

      if (window.location.pathname !== '/prijava') {
        /*
         * Carry where they were through the login page.
         *
         * A token quietly expiring mid-shift used to dump the user back on the
         * home screen, so someone halfway through reading a submission had to
         * find it again by hand. `nastavi` is the same parameter a scanned QR
         * sticker uses, and the login page already refuses anything that is not
         * a same-site path, so this cannot become an open redirect.
         */
        const here = window.location.pathname + window.location.search
        window.location.href = `/prijava?nastavi=${encodeURIComponent(here)}`
      }
    }

    /*
     * Only reached when the server sent no message of its own — which it
     * almost always does, already in the right language. These cover the cases
     * where it cannot: a 500 that never got as far as the application, or a
     * throttle response from the edge.
     */
    const fallback: Record<number, TranslationKey> = {
      403: 'error.forbidden',
      404: 'error.notFound',
      419: 'error.sessionExpired',
      429: 'error.tooManyRequests',
      500: 'error.server',
    }

    const key = fallback[status]

    /*
     * A module the company has switched off.
     *
     * Announced rather than handled here: this interceptor has no idea what is
     * on screen, and whether the refusal should replace the page or only one
     * tile on it is the shell's decision — see AppShell. The name is checked
     * against the four this client knows, so an unexpected body cannot steer
     * the screen into a state nothing renders.
     */
    const module = status === 403 && isModuleName(data?.module) ? data.module : null

    /*
     * The company started requiring two-factor for administrators while this
     * one was signed in. The session still says it is not required, so the
     * gate that would explain it never shows and the page fills with errors.
     * Announced like a switched-off module, for the shell to re-read the
     * session once.
     */
    if (status === 403 && (data as { twoFactorRequired?: unknown } | undefined)?.twoFactorRequired === true && typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent(TWO_FACTOR_REQUIRED_EVENT))
    }

    if (module && typeof window !== 'undefined') {
      window.dispatchEvent(
        new CustomEvent<ModuleDisabledDetail>(MODULE_DISABLED_EVENT, { detail: { module } }),
      )
    }

    throw new ApiError(
      data?.message ?? (key ? t(key) : t('error.generic')),
      status,
      data?.errors ?? {},
      false,
      module,
    )
  },
)
